import React, { useState, useEffect } from 'react';
import {
  Star, MapPin, Briefcase, Clock, Envelope, Phone, ChatCircle,
  PaperPlaneTilt, X, ArrowLeft, CheckCircle, ShieldCheck, CaretDown,
  ArrowsClockwise, ChatCircleDots, ClipboardText
} from '@phosphor-icons/react';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ContractorProfileModal({ contractorId, onClose, onMessage, onReview, homeownerLeads, onQuoteRequested }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [selectedLead, setSelectedLead] = useState('');
  const [quoteMessage, setQuoteMessage] = useState('');
  const [sendingQuote, setSendingQuote] = useState(false);
  const [sentRequests, setSentRequests] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/homeowner/contractor-profile/${contractorId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success) setProfile(data.profile);
        }
      } catch (err) { console.error('Profile fetch error:', err); }
      finally { setLoading(false); }
    })();
  }, [contractorId]);

  // Fetch existing quote requests to this contractor
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/homeowner/quote-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            const sent = new Set();
            (data.requests || []).forEach(r => {
              if (r.contractor_id === contractorId) sent.add(r.lead_id);
            });
            setSentRequests(sent);
          }
        }
      } catch { /* ignore */ }
    })();
  }, [contractorId]);

  const handleSendQuoteRequest = async () => {
    if (!selectedLead) { toast({ title: 'Select a project', variant: 'destructive' }); return; }
    setSendingQuote(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/homeowner/request-quote`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractor_id: contractorId, lead_id: selectedLead, message: quoteMessage || null })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({ title: 'Quote Requested!', description: `${profile?.name || 'Contractor'} has been notified.` });
        setSentRequests(prev => new Set([...prev, selectedLead]));
        setShowQuoteForm(false);
        setSelectedLead('');
        setQuoteMessage('');
        if (onQuoteRequested) onQuoteRequested();
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to send request', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally { setSendingQuote(false); }
  };

  const availableLeads = (homeownerLeads || []).filter(l => !sentRequests.has(l.id));
  const allRequestsSent = homeownerLeads && homeownerLeads.length > 0 && availableLeads.length === 0;

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="contractor-profile-modal">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white dark:bg-[#0F172A] border-4 border-black p-8">
          <ArrowsClockwise size={32} weight="bold" className="animate-spin text-[#2B4C6F] mx-auto" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="contractor-profile-modal">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white dark:bg-[#0F172A] border-4 border-black p-8 text-center">
          <p className="font-bold text-neutral-900 dark:text-white">Profile not found</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-neutral-200 dark:bg-neutral-700 font-bold text-sm">Close</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'about', label: 'About' },
    { id: 'portfolio', label: `Portfolio${profile.portfolio.length ? ` (${profile.portfolio.length})` : ''}` },
    { id: 'reviews', label: `Reviews${profile.reviews.length ? ` (${profile.reviews.length})` : ''}` }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="contractor-profile-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[85vh] bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#2563EB] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-[#0F172A] p-5 border-b-2 border-black flex-shrink-0">
          <button onClick={onClose} className="absolute top-3 right-3 text-neutral-400 hover:text-white z-10" data-testid="profile-close-btn">
            <X size={22} weight="bold" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-[#2563EB] border-2 border-white flex items-center justify-center overflow-hidden flex-shrink-0">
              {profile.profile_image ? (
                <img src={profile.profile_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <Briefcase size={28} weight="bold" className="text-white" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-black text-white uppercase truncate">{profile.name}</h2>
              {profile.company && <p className="text-[#2B4C6F] font-bold text-sm">{profile.company}</p>}
              <div className="flex items-center gap-3 mt-1">
                {profile.rating > 0 && (
                  <span className="flex items-center gap-1 text-sm">
                    <Star size={16} weight="fill" className="text-amber-400" />
                    <span className="text-white font-bold">{Number(profile.rating).toFixed(1)}</span>
                    <span className="text-neutral-400">({profile.reviews_count})</span>
                  </span>
                )}
                {profile.verified && (
                  <span className="flex items-center gap-1 text-xs text-emerald-400 font-bold">
                    <ShieldCheck size={14} weight="bold" /> Verified
                  </span>
                )}
                {profile.completed_jobs > 0 && (
                  <span className="text-xs text-neutral-400">{profile.completed_jobs} jobs done</span>
                )}
              </div>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => { onClose(); onMessage(profile.id, profile.name, profile.profile_image); }}
              className="flex-1 h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] text-xs font-bold uppercase tracking-wider border-2 border-white/30 flex items-center justify-center gap-2 transition-colors"
              data-testid="profile-message-btn"
            >
              <ChatCircleDots size={16} weight="bold" /> Message
            </button>
            <button
              onClick={() => setShowQuoteForm(!showQuoteForm)}
              disabled={allRequestsSent}
              className={`flex-1 h-10 text-xs font-bold uppercase tracking-wider border-2 border-white/30 flex items-center justify-center gap-2 transition-colors ${
                allRequestsSent
                  ? 'bg-neutral-600 text-neutral-400 cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
              data-testid="profile-request-quote-btn"
            >
              <ClipboardText size={16} weight="bold" /> {allRequestsSent ? 'Quotes Sent' : 'Request Quote'}
            </button>
            {onReview && (
              <button
                onClick={() => { onClose(); onReview(); }}
                className="flex-1 h-10 bg-[#3B82F6] hover:bg-[#E55D00] text-white text-xs font-bold uppercase tracking-wider border-2 border-white/30 flex items-center justify-center gap-2 transition-colors"
                data-testid="profile-review-btn"
              >
                <Star size={16} weight="bold" /> Review
              </button>
            )}
          </div>

          {/* Quote Request Form */}
          {showQuoteForm && (
            <div className="mt-3 p-3 bg-white/10 border border-white/20" data-testid="quote-request-form">
              <p className="text-white text-xs font-bold uppercase mb-2">Select a project:</p>
              {availableLeads.length === 0 ? (
                <p className="text-neutral-400 text-xs">No projects available to request a quote for.</p>
              ) : (
                <>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto mb-2">
                    {availableLeads.map((lead) => (
                      <label
                        key={lead.id}
                        className={`flex items-center gap-2 p-2 cursor-pointer border transition-colors ${
                          selectedLead === lead.id
                            ? 'border-emerald-400 bg-emerald-900/30'
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="quote-lead"
                          value={lead.id}
                          checked={selectedLead === lead.id}
                          onChange={() => setSelectedLead(lead.id)}
                          className="accent-emerald-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-bold truncate">{lead.job_type_display}</p>
                          <p className="text-neutral-400 text-[10px]">{lead.location?.city}{lead.location?.state ? `, ${lead.location.state}` : ''}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  <textarea
                    value={quoteMessage}
                    onChange={(e) => setQuoteMessage(e.target.value)}
                    placeholder="Add a message (optional)..."
                    rows={2}
                    className="w-full px-2 py-1.5 text-xs bg-white/10 border border-white/20 text-white placeholder-neutral-500 resize-none focus:outline-none focus:border-emerald-400 mb-2"
                    data-testid="quote-message-input"
                  />
                  <button
                    onClick={handleSendQuoteRequest}
                    disabled={!selectedLead || sendingQuote}
                    className="w-full h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider border border-emerald-400 disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
                    data-testid="quote-send-btn"
                  >
                    {sendingQuote ? (
                      <><ArrowsClockwise size={14} weight="bold" className="animate-spin" /> Sending...</>
                    ) : (
                      <><PaperPlaneTilt size={14} weight="bold" /> Send Request</>
                    )}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b-2 border-black flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#2563EB] text-[#0F172A] border-b-2 border-[#2B4C6F]'
                  : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700'
              }`}
              data-testid={`profile-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'about' && (
            <div className="space-y-4" data-testid="profile-about">
              {profile.bio && (
                <div>
                  <p className="text-xs font-bold uppercase text-neutral-500 mb-1">About</p>
                  <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">{profile.bio}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {profile.location && (
                  <div className="flex items-center gap-2 p-2 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                    <MapPin size={16} weight="bold" className="text-[#2B4C6F]" />
                    <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">{profile.location}</span>
                  </div>
                )}
                {profile.experience && (
                  <div className="flex items-center gap-2 p-2 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                    <Briefcase size={16} weight="bold" className="text-[#475569]" />
                    <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">{profile.experience}</span>
                  </div>
                )}
                {profile.phone && (
                  <div className="flex items-center gap-2 p-2 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                    <Phone size={16} weight="bold" className="text-emerald-500" />
                    <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">{profile.phone}</span>
                  </div>
                )}
                {profile.email && (
                  <div className="flex items-center gap-2 p-2 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                    <Envelope size={16} weight="bold" className="text-purple-500" />
                    <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300 truncate">{profile.email}</span>
                  </div>
                )}
              </div>
              {profile.skills && profile.skills.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase text-neutral-500 mb-2">Skills</p>
                  <div className="flex flex-wrap gap-1.5">
                    {profile.skills.map((skill, i) => (
                      <span key={i} className="px-2 py-1 bg-[#2563EB]/10 text-[#2B4C6F] text-xs font-bold border border-[#2B4C6F]/30">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {!profile.bio && !profile.location && (!profile.skills || !profile.skills.length) && (
                <p className="text-sm text-neutral-400 text-center py-6">No additional details available yet.</p>
              )}
            </div>
          )}

          {activeTab === 'portfolio' && (
            <div data-testid="profile-portfolio">
              {profile.portfolio.length === 0 ? (
                <p className="text-sm text-neutral-400 text-center py-8">No portfolio items yet.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {profile.portfolio.map((item) => (
                    <div key={item.id} className="border-2 border-black overflow-hidden group">
                      {item.image_url && (
                        <div className="aspect-square bg-neutral-200 dark:bg-neutral-800 overflow-hidden">
                          <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        </div>
                      )}
                      <div className="p-2">
                        <p className="text-xs font-bold text-neutral-900 dark:text-white truncate">{item.title}</p>
                        {item.category && <p className="text-[10px] text-neutral-500">{item.category}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div data-testid="profile-reviews">
              {profile.reviews.length === 0 ? (
                <p className="text-sm text-neutral-400 text-center py-8">No reviews yet. Be the first to leave a review!</p>
              ) : (
                <div className="space-y-3">
                  {profile.reviews.map((review) => (
                    <div key={review.id} className="p-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          {[1,2,3,4,5].map((s) => (
                            <Star key={s} size={14} weight={s <= review.rating ? 'fill' : 'regular'}
                              className={s <= review.rating ? 'text-amber-400' : 'text-neutral-300'} />
                          ))}
                        </div>
                        <span className="text-[10px] text-neutral-400">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-xs font-bold text-neutral-900 dark:text-white">{review.title}</p>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1 leading-relaxed">{review.content}</p>
                      <p className="text-[10px] text-neutral-400 mt-2">
                        — {review.reviewer_name}{review.reviewer_type ? ` (${review.reviewer_type})` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

